import React, { Component, createRef } from 'react';

// CSS
import '../Subscriptions/Subscription.scss';
import './Welcome.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, platformInfo } from '../../Helper/Api';

// Components
import { Toast } from 'primereact/toast';

// Properties
const properties = require('../../properties.json');

const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The six things to do first, in the order they have to happen. Data rather
// than markup so the list reads as a list and the numbering cannot drift.
// `key` ties a step to the check in Welcome.stepsDone(); a step with no key is
// one this page has no data to judge, so it is never shown as done or not done.
const STEPS = [
    { key: 'plan', to: '/billing', icon: 'pi pi-credit-card', title: 'اختار أو عدّل اشتراكك', text: 'قبل أي خطوة تانية.' },
    { key: 'branding', to: '/settings/branding', icon: 'pi pi-palette', title: 'اضبط هوية موقعك', text: 'الألوان، العنوان الرئيسي، صورتك، وصفحة "عني".' },
    { key: 'contact', to: '/settings/contact-info', icon: 'pi pi-phone', title: 'أضف بيانات التواصل', text: 'موبايل، بريد، وروابط السوشيال.' },
    { to: '/onboarding', icon: 'pi pi-sitemap', title: 'المراحل والمواد والفترات', text: 'لو لسه ما أضفتهاش.' },
    { key: 'course', to: '/courses/add', icon: 'pi pi-book', title: 'أنشئ أول كورس', text: 'أقسام ودروس (فيديو، صوت، صور، ملفات).' },
    { key: 'students', to: '/students', icon: 'pi pi-users', title: 'فعّل الطلاب', text: 'وافق على طلبات الاشتراك اللي بتوصلك.' },
];

// The how-to topics, in the same order as their panels in render().
const HELP_TOPICS = [
    { icon: 'pi pi-palette', title: 'ضبط هوية الموقع' },
    { icon: 'pi pi-user', title: 'إزاي الطالب يستخدم الموقع؟' },
    { icon: 'pi pi-desktop', title: 'نسخة الديسك توب (للمدرس والطالب)' },
    { icon: 'pi pi-book', title: 'إدارة الكورسات والطلاب' },
    { icon: 'pi pi-bolt', title: 'نصائح سريعة' },
];

/** A number that counts up to its value once, when it first appears. */
class CountUp extends Component {
    state = { v: 0 };

    componentDidMount() {
        const to = Number(this.props.to) || 0;
        if (reduceMotion() || to === 0) { this.setState({ v: to }); return; }
        const start = performance.now();
        const tick = (t) => {
            const k = Math.min(1, (t - start) / 1100);
            this.setState({ v: to * (1 - Math.pow(1 - k, 3)) });
            if (k < 1) this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }

    componentWillUnmount() { if (this.raf) cancelAnimationFrame(this.raf); }

    render() { return this.props.format(this.state.v); }
}

/** A bar that fills to its value after it is on screen, rather than arriving full. */
class Fill extends Component {
    state = { on: false };
    componentDidMount() { this.raf = requestAnimationFrame(() => this.setState({ on: true })); }
    componentWillUnmount() { if (this.raf) cancelAnimationFrame(this.raf); }
    render() {
        return (
            <span className="w-bar" aria-hidden="true">
                <span className="w-barfill" style={{ width: this.state.on ? `${this.props.pct}%` : 0 }} />
            </span>
        );
    }
}

/** The hero's progress ring: the share of the trackable setup steps that are done. */
class Ring extends Component {
    state = { p: 0 };

    componentDidMount() { this.run(); }
    componentDidUpdate(prev) { if (prev.done !== this.props.done || prev.total !== this.props.total) this.run(); }
    componentWillUnmount() { if (this.raf) cancelAnimationFrame(this.raf); }

    run = () => {
        const { done, total } = this.props;
        const goal = total ? (done / total) * 100 : 0;
        if (this.raf) cancelAnimationFrame(this.raf);
        if (reduceMotion()) { this.setState({ p: goal }); return; }
        const from = this.state.p;
        const start = performance.now();
        const tick = (t) => {
            const k = Math.min(1, (t - start) / 1200);
            this.setState({ p: from + (goal - from) * (1 - Math.pow(1 - k, 3)) });
            if (k < 1) this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    };

    render() {
        const { done, total } = this.props;
        return (
            <div className="w-ring" style={{ '--p': this.state.p }} role="img" aria-label={`${done} من ${total} خطوات إعداد مكتملة`}>
                <div className="w-ringin">
                    <span className="w-ringnum"><b>{done}</b><small>/{total}</small></span>
                    <span className="w-ringlbl">خطوات مكتملة</span>
                </div>
            </div>
        );
    }
}

// The teacher's landing page: where their links are, what to do first, how students use the site,
// and a direct WhatsApp line to platform support. The support number comes from the platform
// (GET /lookup/platform-info), so it is never hard-coded here.
class Welcome extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
        this.pollTimer = null;
    }

    state = {
        loading: true,
        me: null,
        baseDomain: '',
        supportWhatsapp: '',
        siteReady: null, // null = unknown/checking, true = reachable, false = not yet
        usage: null,     // { students, storageBytes, courses } each { used, limit }
        plan: null,      // GET /billing/me -- shown first so the teacher sets the subscription before anything else
        endAt: null,     // client-clock ms when the preparation estimate runs out
        totalMs: null,
        now: Date.now(),
        help: null,      // which how-to topic is open, or none
    }

    componentDidMount() {
        document.title = properties.site_name + " | الرئيسية";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        Promise.all([
            apiFetch("/teachers/me"),
            platformInfo().catch(() => ({})),
        ]).then(([me, info]) => {
            this.setState({
                me,
                baseDomain: (info && info.baseDomain) || '',
                supportWhatsapp: (info && info.supportWhatsapp) || '',
                loading: false,
            }, this.startPolling);
            apiFetch("/teachers/me/usage").then(usage => this.setState({ usage })).catch(() => {});
            apiFetch("/billing/me").then(plan => this.setState({ plan })).catch(() => {});
        }).catch(error => {
            this.setState({ loading: false });
            console.error('Error:', error);
        });
    }

    componentWillUnmount() {
        if (this.pollTimer) clearInterval(this.pollTimer);
        if (this.tickTimer) clearInterval(this.tickTimer);
    }

    siteHost = () => {
        const { me, baseDomain } = this.state;
        if (!me) return null;
        if (me.customDomain && me.customDomainStatus === 'VERIFIED') return me.customDomain;
        if (me.subdomain && baseDomain) return `${me.subdomain}.${baseDomain}`;
        return null;
    }

    adminHost = () => {
        const { me, baseDomain } = this.state;
        if (!me) return null;
        if (me.customDomain && me.customDomainStatus === 'VERIFIED') return `admin.${me.customDomain}`;
        if (me.subdomain && baseDomain) return `admin-${me.subdomain}.${baseDomain}`;
        return null;
    }

    // A cross-origin no-cors fetch resolves only when the host answers over valid HTTPS, which is
    // exactly "the site is live" -- it rejects while DNS/certificate are still being prepared.
    checkSite = () => {
        const host = this.siteHost();
        if (!host) return;
        fetch(`https://${host}/`, { mode: 'no-cors', cache: 'no-store' })
            .then(() => {
                this.setState({ siteReady: true });
                if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
            })
            .catch(() => this.setState({ siteReady: false }));
    }

    startPolling = () => {
        const host = this.siteHost();
        if (!host) return;
        this.checkSite();
        this.pollTimer = setInterval(this.checkSite, 10000);
        // how long ago the link was chosen -> remaining preparation time
        apiFetch("/lookup/site-status", { query: { host } }).then(st => {
            if (!st || st.known === false) return;
            const eta = (st.etaSeconds || 120) * 1000;
            const elapsed = st.requestedAtMillis ? Math.max(0, st.nowMillis - st.requestedAtMillis) : 0;
            this.setState({ endAt: Date.now() + Math.max(0, eta - elapsed), totalMs: eta });
            this.tickTimer = setInterval(() => this.setState({ now: Date.now() }), 1000);
        }).catch(() => {});
    }

    remainingText = () => {
        const { endAt, now } = this.state;
        if (endAt === null) return '';
        const s = Math.max(0, Math.ceil((endAt - now) / 1000));
        if (s === 0) return 'قريبًا جدًا… بنخلّص آخر خطوة';
        const pad = (n) => (n < 10 ? '0' : '') + n;
        return `الوقت المتبقي التقريبي: ${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
    }

    /** How far through the preparation estimate we are -- the same two numbers the countdown reads. */
    preparedPct = () => {
        const { endAt, totalMs, now } = this.state;
        if (endAt === null || !totalMs) return null;
        const left = Math.max(0, endAt - now);
        return Math.min(100, Math.max(0, Math.round(((totalMs - left) / totalMs) * 100)));
    }

    copy = (text, label) => {
        const done = () => this.toast.current.show({ severity: 'success', summary: 'تم النسخ', detail: label, life: 2500 });
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => window.prompt('انسخ من هنا:', text));
        } else {
            window.prompt('انسخ من هنا:', text);
        }
    }

    studentMessage = () => {
        const host = this.siteHost();
        const name = (this.state.me && this.state.me.name) || '';
        return [
            `أهلاً بك في منصة ${name}`,
            ``,
            `1) افتح الموقع: https://${host}`,
            `2) اضغط "Create Account" وأنشئ حسابك.`,
            `3) من صفحة Courses اختر الكورس واضغط "Request Subscription" لطلب الاشتراك.`,
            `4) بعد ما أوافق على طلبك يفتح لك الكورس في حسابك.`,
            `5) الفيديوهات المدفوعة بتتشغل من تطبيق الديسكتوب فقط — حمّله من الموقع (Download Desktop App) وسجّل دخول بنفس حسابك.`,
            ``,
            `لو واجهتك أي مشكلة كلمني.`,
        ].join('\n');
    }

    supportLink = () => {
        const { supportWhatsapp, me } = this.state;
        const number = (supportWhatsapp || '').replace(/[^0-9]/g, '');
        if (!number) return null;
        const host = this.siteHost() || this.adminHost() || '';
        const text = `مرحبًا، أنا ${me && me.name ? me.name : 'مدرّس'} وعندي مشكلة/استفسار بخصوص المنصة.${host ? `\nموقعي: https://${host}` : ''}`;
        return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
    }

    /* Which setup steps are visibly done, judged only from what this page has
     * already loaded -- no extra request. A step whose data has not arrived yet
     * (plan, usage) counts as not done until it does; the stages step has no
     * data here at all, so it is left out of the count rather than guessed. */
    stepsDone = () => {
        const { me, plan, usage } = this.state;
        const b = (me && me.branding) || {};
        const used = (m) => !!(m && m.used > 0);
        return {
            plan: !!(plan && plan.state && plan.state !== 'NONE'),
            branding: !!(b.primaryColor || b.accentColor || b.heroTitle || b.tagline || b.siteTitle || b.aboutJson || b.faviconUrl || b.heroImageUrl),
            contact: !!(me && (me.location || me.facebookUrl || me.instagramUrl || me.linkedinUrl || me.behanceUrl)),
            course: used(usage && usage.courses),
            students: used(usage && usage.students),
        };
    }

    // The subscription box: nothing on the site really works until the plan is set, so it says so
    // loudly and leads straight to "اشتراكي".
    renderPlan = () => {
        const p = this.state.plan;
        if (!p) return null;
        const go = () => { window.location.href = '/billing'; };
        const egp = (v) => (v === null || v === undefined ? '' : `${Number(v).toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ج.م`);
        let tone, icon, title, text, button;
        if (p.state === 'NONE') {
            tone = 'todo';
            icon = 'pi pi-exclamation-triangle';
            title = 'الخطوة الأولى: اختار اشتراكك قبل أي حاجة';
            text = p.trialOffer
                ? `قبل ما تضيف كورسات أو طلاب، لازم تحدد اشتراكك. متاح لك تجربة مجانية ${p.trialOffer.days} يوم (${p.trialOffer.storageGb} جيجا، ${p.trialOffer.students} طالب، ${p.trialOffer.videos} فيديو) تقدر تبدأها دلوقتي.`
                : 'قبل ما تضيف كورسات أو طلاب، لازم تحدد اشتراكك: المساحة، وعدد الطلاب، وعدد الفيديوهات. من غير اشتراك مش هتقدر ترفع محتوى.';
            button = p.trialOffer ? 'ابدأ التجربة المجانية / اختار اشتراكك' : 'اختار اشتراكك الآن';
        } else if (p.state === 'SUSPENDED' || p.state === 'GRACE') {
            tone = 'stop';
            icon = p.state === 'SUSPENDED' ? 'pi pi-ban' : 'pi pi-exclamation-triangle';
            title = p.state === 'SUSPENDED' ? 'اشتراكك موقوف' : 'اشتراكك انتهى — في فترة سماح';
            text = p.message || 'جدّد اشتراكك علشان موقعك وطلابك يفضلوا شغالين.';
            button = 'جدّد اشتراكك الآن';
        } else {
            tone = p.renewSoon ? 'todo' : 'ok';
            icon = 'pi pi-check-circle';
            title = p.trial ? 'إنت في التجربة المجانية' : 'اشتراكك فعّال';
            text = `${p.storageGb} جيجا · ${p.students} طالب · ${p.videos} فيديو`
                + (p.daysLeft !== null && p.daysLeft !== undefined ? ` — باقي ${p.daysLeft} يوم` : '')
                + (p.balanceEgp !== null && p.balanceEgp !== undefined ? ` — رصيدك ${egp(p.balanceEgp)}` : '')
                + '. راجع اشتراكك وزوّده لو محتاج قبل ما تبدأ.';
            if (p.message) text += ` ${p.message}`;
            button = 'تعديل اشتراكي';
        }
        return (
            <div className={`w-plan is-${tone}`}>
                <span className="w-plan-ic" aria-hidden="true"><i className={icon} /></span>
                <div className="w-plan-tx">
                    <h2>{title}</h2>
                    <p>{text}</p>
                </div>
                <button type="button" className="ui-btn w-plan-btn" onClick={go}>
                    <i className="pi pi-credit-card" /> {button}
                </button>
            </div>
        );
    }

    /** The links tile: is the site up, and the two addresses with their copy buttons. */
    renderLinks = () => {
        const { siteReady } = this.state;
        const site = this.siteHost();
        const admin = this.adminHost();
        const pct = this.preparedPct();

        if (!site) {
            return (
                <div className="w-tile w-links w-nolink" style={{ '--i': 0 }}>
                    <span className="w-nolinkic" aria-hidden="true"><i className="pi pi-link" /></span>
                    <span className="w-nolinktx">
                        <b>لسه ما اخترتش رابطك الخاص</b>
                        <p>اختاره عشان طلابك يقدروا يدخلوا موقعك.</p>
                    </span>
                    <button type="button" className="ui-btn is-gold" onClick={() => { window.location.href = '/onboarding'; }}>
                        <i className="pi pi-link" /> اختيار الرابط
                    </button>
                </div>
            );
        }

        return (
            <div className="w-tile w-links" style={{ '--i': 0 }}>
                <div className={`w-sitestate ${siteReady ? 'is-ok' : 'is-wait'}`}>
                    <span className="w-dot" aria-hidden="true" />
                    <span>
                        <b>{siteReady === true ? 'موقعك شغال ومتاح للطلاب دلوقتي' : 'موقعك قيد التجهيز'}</b>
                        {siteReady !== true && (
                            <small>بيستغرق حوالي دقيقتين بعد اختيار الرابط، والصفحة بتتحدث لوحدها أول ما يجهز. {this.remainingText()}</small>
                        )}
                    </span>
                </div>
                {siteReady !== true && pct !== null && <Fill pct={pct} />}

                <div className="w-linkrows">
                    <div className="w-linkrow">
                        <span className="w-linkic" aria-hidden="true"><i className="pi pi-globe" /></span>
                        <span className="w-linktx">
                            <b>موقع الطلاب</b>
                            <a href={`https://${site}`} target="_blank" rel="noopener noreferrer" dir="ltr">https://{site}</a>
                        </span>
                        <button type="button" className="w-iconbtn" title="نسخ" aria-label="نسخ رابط موقع الطلاب"
                            onClick={() => this.copy(`https://${site}`, 'تم نسخ رابط موقع الطلاب')}>
                            <i className="pi pi-copy" />
                        </button>
                    </div>
                    <div className="w-linkrow">
                        <span className="w-linkic" aria-hidden="true"><i className="pi pi-desktop" /></span>
                        <span className="w-linktx">
                            <b>لوحة التحكم <small>دي اللي إنت فيها</small></b>
                            <a href={`https://${admin}`} target="_blank" rel="noopener noreferrer" dir="ltr">https://{admin}</a>
                        </span>
                        <button type="button" className="w-iconbtn" title="نسخ" aria-label="نسخ رابط لوحة التحكم"
                            onClick={() => this.copy(`https://${admin}`, 'تم نسخ رابط لوحة التحكم')}>
                            <i className="pi pi-copy" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /** One tile per figure of the plan: watermark glyph, counted-up number, filling bar. */
    renderUsage = () => {
        const { usage } = this.state;
        if (!usage) return null;

        const mb = (v) => `${(v / 1048576).toFixed(v >= 1073741824 ? 0 : 1)} ميجا`;
        const rows = [
            ['الطلاب', usage.students, 'pi pi-users', 'is-teal', (v) => `${Math.round(v)}`],
            ['مساحة التخزين', usage.storageBytes, 'pi pi-database', 'is-gold', mb],
            ['الكورسات', usage.courses, 'pi pi-book', 'is-green', (v) => `${Math.round(v)}`],
            ['الفيديوهات', usage.videos, 'pi pi-video', 'is-red', (v) => `${Math.round(v)}`],
        ].filter(([, m]) => m);

        return rows.map(([label, m, icon, tone, fmt], i) => {
            // limit -1 = not limited (no subscription yet)
            const unlimited = m.limit < 0;
            const pct = !unlimited && m.limit > 0 ? Math.min(100, Math.round((m.used / m.limit) * 100)) : 0;
            const near = !unlimited && pct >= 90;
            return (
                <div className={`w-tile w-stat ${tone} ${near ? 'is-near' : ''}`} key={label} style={{ '--i': i + 1 }}>
                    <span className="w-wm" aria-hidden="true"><i className={icon} /></span>
                    <span className="w-k">{label}{near ? ' ⚠️' : ''}</span>
                    <span className="w-v"><CountUp to={m.used} format={fmt} /></span>
                    <span className="w-of">من {unlimited ? 'بلا حد' : fmt(m.limit)}</span>
                    {!unlimited && <Fill pct={pct} />}
                </div>
            );
        });
    }

    render() {
        const { loading, me, help } = this.state;
        const site = this.siteHost();
        const support = this.supportLink();
        const done = this.stepsDone();
        const tracked = STEPS.filter((s) => s.key);
        const doneCount = tracked.filter((s) => done[s.key]).length;
        // the first trackable step not done yet, in the list's own order
        const next = tracked.find((s) => !done[s.key]) || null;

        return (
            <>
                <Toast ref={this.toast}></Toast>
                <div className="content welcome-page">
                    <div className="container">
                        {loading && (
                            <div className="w-skeleton" aria-hidden="true">
                                <span className="s-hero" />
                                <span className="s-grid"><i /><i /><i /><i /></span>
                            </div>
                        )}

                        {!loading && me && (
                            <>
                                {/* ---- the command centre: greeting, the ways out, and how far setup has got ---- */}
                                <section className="w-hero">

                                    <div className="w-greet">
                                        <h1>أهلاً {me.name || ''} <span className="w-wave">👋</span></h1>
                                        <p>
                                            دي صفحتك الرئيسية: روابطك، وأول الخطوات، وشرح استخدام المنصة ليك ولطلابك.
                                        </p>
                                        <div className="w-actions">
                                            {site && (
                                                <button type="button" className="w-btn is-gold"
                                                    onClick={() => this.copy(this.studentMessage(), 'تم نسخ الرسالة، ابعتها لطلابك')}>
                                                    <i className="pi pi-whatsapp" /> نسخ رسالة جاهزة للطلاب
                                                </button>
                                            )}
                                            {site && (
                                                <a className="w-btn is-soft" href={`https://${site}`} target="_blank" rel="noopener noreferrer">
                                                    <i className="pi pi-external-link" /> فتح موقعي
                                                </a>
                                            )}
                                            {support && (
                                                <a className="w-btn is-soft" href={support} target="_blank" rel="noopener noreferrer">
                                                    <i className="pi pi-comments" /> الدعم
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {/* the setup panel: how far along, and the one thing to do next */}
                                    <div className="w-setup">
                                        <Ring done={doneCount} total={tracked.length} />
                                        <div className="w-next">
                                            {next ? (
                                                <>
                                                    <span className="w-nextk">الخطوة الجاية</span>
                                                    <b>{next.title}</b>
                                                    <a className="w-nextgo" href={next.to}>
                                                        يلا نبدأ <i className="pi pi-arrow-left" />
                                                    </a>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="w-nextk">الإعداد</span>
                                                    <b>كل الخطوات خلصت 🎉</b>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {this.renderPlan()}

                                {/* ---- the bento: links, then one tile per figure of the plan ---- */}
                                {/* links on one side, the plan's figures as a 2x2 on the other,
                                    so both halves end on the same line */}
                                <section className="w-bento">
                                    {this.renderLinks()}
                                    {this.state.usage && <div className="w-stats">{this.renderUsage()}</div>}
                                </section>

                                {this.state.usage && (
                                    <p className="w-more">
                                        لو قربت من أي حد وعايز تزوده، اطلب زيادة من <a href="/billing">اشتراكي</a>.
                                    </p>
                                )}

                                {/* ---- what to do, in order, with what is already done ticked ---- */}
                                <section className="w-block">
                                    <header className="w-blockhead">
                                        <h2><i className="pi pi-flag" /> ابدأ من هنا</h2>
                                        <span className="w-count">{doneCount} من {tracked.length} مكتملة</span>
                                    </header>

                                    <ol className="w-steps">
                                        {STEPS.map((s, i) => {
                                            const state = !s.key ? 'is-free' : done[s.key] ? 'is-done' : 'is-todo';
                                            return (
                                                <li key={s.to} className={state} style={{ '--i': i }}>
                                                    <a href={s.to}>
                                                        <span className="n">{state === 'is-done' ? <i className="pi pi-check" /> : i + 1}</span>
                                                        <span className="tx">
                                                            <b>{s.title}</b>
                                                            <small>{s.text}</small>
                                                        </span>
                                                        <span className="ic" aria-hidden="true"><i className={s.icon} /></span>
                                                        <i className="pi pi-angle-left go" aria-hidden="true" />
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </section>

                                {/* ---- the manual, folded away until it is wanted ---- */}
                                <section className="w-block">
                                    <header className="w-blockhead">
                                        <h2><i className="pi pi-question-circle" /> شرح الاستخدام</h2>
                                    </header>

                                    {/* one row of topics; picking one opens its answer under the row, picking it again closes it */}
                                    <div className="w-helptabs" role="group" aria-label="مواضيع الشرح">
                                        {HELP_TOPICS.map((t, n) => (
                                            <button
                                                type="button"
                                                key={t.title}
                                                className={`w-helptab ${help === n ? "is-on" : ""}`}
                                                aria-expanded={help === n}
                                                aria-controls={`w-help-${n}`}
                                                onClick={() => this.setState({ help: help === n ? null : n })}
                                            >
                                                <i className={t.icon} /> {t.title}
                                                <i className="pi pi-angle-down w-helpchev" aria-hidden="true" />
                                            </button>
                                        ))}
                                    </div>

                                    <div className="w-help">
                                        {help === 0 && (
                                            <div className="w-helppanel" id="w-help-0" role="region">
                                            <ul>
                                                <li>من القائمة: <b>الإعدادات ← هوية الموقع</b>.</li>
                                                <li>اختار لونك الأساسي ولون التمييز، واكتب عنوان صفحتك الرئيسية والنص التعريفي.</li>
                                                <li>أي حقل تسيبه فاضي هيظهر بالشكل الافتراضي، وأي قسم فاضي بيتخفي من الموقع.</li>
                                                <li>الصور (الأيقونة، صورة الواجهة، صورتك) بترفعها من جهازك مباشرة. صفحة "عني" بتظهر لما تكتب فيها أي حاجة.</li>
                                                <li>بعد الحفظ حدّث موقع الطلاب وهتشوف التغيير فورًا.</li>
                                            </ul>
                                            </div>
                                        )}
                                        {help === 1 && (
                                            <div className="w-helppanel" id="w-help-1" role="region">
                                            <ol>
                                                <li>يفتح رابط موقعك ويضغط <b>Create Account</b> وينشئ حسابه.</li>
                                                <li>يدخل صفحة <b>Courses</b> ويختار الكورس ويضغط <b>Request Subscription</b>.</li>
                                                <li>بيوصلك الطلب هنا في <b>الطلاب ← تفعيل الطلاب</b> وبتوافق عليه.</li>
                                                <li>بعد الموافقة بيفتح له محتوى الكورس. لو الدرس "معاينة مجانية" بيشوفه الكل حتى من غير اشتراك.</li>
                                                <li>لو طلب الاشتراك في كورس هو مشترك فيه، أو طلبه لسه قيد المراجعة، بتظهر له رسالة بتوضح ده.</li>
                                            </ol>
                                            </div>
                                        )}
                                        {help === 2 && (
                                            <div className="w-helppanel" id="w-help-2" role="region">
                                            <ul>
                                                <li>الفيديوهات والمحتوى المدفوع محمية، وبتتشغل <b>من تطبيق الديسكتوب فقط</b> (مش من المتصفح). ده بيحمي محتواك من النسخ والتصوير.</li>
                                                <li>الطالب بينزّل التطبيق من زر <b>Download Desktop App</b> في موقعك، ويسجّل دخول بنفس حسابه.</li>
                                                <li>الزر ده بيظهر لما تضيف رابط التحميل في <b>هوية الموقع ← رابط التحميل</b>. لو محتاج نسخة التطبيق، كلّم الدعم من الزر تحت.</li>
                                                <li>التطبيق بيشتغل على ويندوز 10 أو 11. التثبيت بخطوات "موافق".</li>
                                                <li>التطبيق بيمنع تصوير الشاشة. لو الطالب حاول أكتر من مرة بيتحظر حسابه، وبتشوف ده في <b>تنبيهات الأمان</b> وتقدر تفك الحظر.</li>
                                            </ul>
                                            </div>
                                        )}
                                        {help === 3 && (
                                            <div className="w-helppanel" id="w-help-3" role="region">
                                            <ul>
                                                <li><b>الكورسات:</b> قسّم الكورس لأقسام، وكل قسم فيه دروس. تقدر تسمح بتحميل ملف معين من تحديد خانة "السماح بالتحميل".</li>
                                                <li><b>الفترات:</b> الاشتراك بيتم على "فترة" مرتبطة بمادة. الطالب المشترك في الفترة بيشوف كورساتها ودروسها وامتحاناتها.</li>
                                                <li><b>الطلاب:</b> من "طلابي" تقدر تعدّل بيانات الطالب أو تعيد تعيين كلمة المرور.</li>
                                                <li><b>الجهاز الواحد:</b> تقدر تحدد عدد الأجهزة المسموحة لكل طالب من الإعدادات.</li>
                                            </ul>
                                            </div>
                                        )}
                                        {help === 4 && (
                                            <div className="w-helppanel" id="w-help-4" role="region">
                                            <ul>
                                                <li>جرّب بنفسك: افتح موقعك من متصفح تاني وأنشئ حساب طالب تجريبي.</li>
                                                <li>ارفع الفيديوهات بصيغة MP4. الملفات الكبيرة بتاخد وقت للرفع والتجهيز.</li>
                                                <li>حافظ على كلمة مرورك، وما تشاركهاش مع حد.</li>
                                                <li>كل أستاذ بياناته منفصلة تمامًا، ومحدش غيرك يشوف طلابك أو كورساتك.</li>
                                            </ul>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/* ---- the way out of any of it ---- */}
                                <section className="w-support">
                                    <span className="w-supportic" aria-hidden="true"><i className="pi pi-whatsapp" /></span>
                                    <div className="w-supporttx">
                                        <b>محتاج مساعدة؟</b>
                                        <p>{support ? 'لو قابلتك أي مشكلة أو عندك سؤال، كلّمنا مباشرة على الواتس اب.' : 'الدعم الفني قريبًا على الواتس اب.'}</p>
                                    </div>
                                    {support && (
                                        <a className="w-whatsapp" href={support} target="_blank" rel="noopener noreferrer">
                                            <i className="pi pi-whatsapp" /> تواصل مع الدعم على واتس اب
                                        </a>
                                    )}
                                </section>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default Welcome;
